import { useTraits } from '@nuxt-common/media-card-large/app/composables/useTraits';
import { useInitSizePDP, useSize as useSizeMCL } from '@nuxt-common/media-card-large/app/modules/size';
import { injectLocal, provideLocal } from '@vueuse/core';
import { useProduct } from '~/modules/product/composables/useProduct';
import { useProductType } from '~/composables/useProductType';
import useConsolidatedLatchkey from '~/modules/latchkey/composables/useConsolidatedLatchkey';
import useFeatureEligibility from '~/modules/featureControl/composables/useFeatureEligibility';
import { usePageData } from '~/composables/usePageData';
import type { Size, SizeProps, SelectedSizeId } from '~/declaration/traits';
import { useConsolidatedMember } from '~/composables/useConsolidatedMember';
import useIsolatedStickyAtbStateStore from '~/modules/sticky-product-traits/stores/useIsolatedStickyAtbStateStore';

/**
 * TODO: Traits initialization should be moved to the Product level
 * @description
 * This composable is responsible for providing the size data for the Product.
 * But it also handles complicated case when the Product is a Consolidated Latchkey.
 */
const SIZES_TRAITS_REPOSITORY = 'sizes-traits';

type SizeReturn = {
  label?: import('vue').ComputedRef<string>;
  selectedSizeId: SelectedSizeId;
  selectedSize: import('vue').WritableComputedRef<Size | null>;
  sizeProps: import('vue').ComputedRef<SizeProps>;
  sizes: import('vue').ComputedRef<Size[]>;
};

function getData(id: string): {
  label?: ComputedRef<string>;
  selectedSizeId: SelectedSizeId;
  selectedSize: WritableComputedRef<Size | null>;
  sizeProps: ComputedRef<SizeProps>;
  sizes: ComputedRef<Size[]>;
} {
  const stickyAtbStore = useIsolatedStickyAtbStateStore();
  const { isConsolidatedLatchKey, isShopTheSet } = useProductType();
  const { isShopTheSetExpEnabled } = useFeatureEligibility();

  if (isShopTheSetExpEnabled && isShopTheSet) {
    const { sizeProps, label, sizes, selectedSizeId, selectedSize } = useConsolidatedMember();
    return { label, sizes, sizeProps, selectedSize, selectedSizeId } as unknown as SizeReturn;
  }

  if (isConsolidatedLatchKey) {
    const { sizeProps, sizes, selectedSizeId, selectedSize } = useConsolidatedLatchkey();

    return {
      sizes,
      sizeProps,
      selectedSize,
      selectedSizeId,
    } as unknown as SizeReturn;
  }

  const sizeProps = useInitSizePDP({
    id,
    data: (usePageData()?.traits?.size?.[id] ??
      (stickyAtbStore.pageDataStickyAtbTraits as unknown as Record<string, Record<string, unknown>> | null)?.size?.[id]) as unknown as Parameters<
      typeof useInitSizePDP
    >[0]['data'],
  });
  const { label, sizes } = useSizeMCL(id);
  const { selectedSizeId, selectedSize } = useTraits(id);

  return {
    label,
    sizes,
    sizeProps,
    selectedSize,
    selectedSizeId,
  };
}

export default function useSize(): {
  label?: ComputedRef<string>;
  selectedSizeId: SelectedSizeId;
  selectedSize: WritableComputedRef<Size | null>;
  sizeProps: ComputedRef<SizeProps>;
  sizes: ComputedRef<Size[]>;
} {
  const { productId: id } = useProduct();
  const resultRepository = `${SIZES_TRAITS_REPOSITORY}-${id}`;
  const providedContext = injectLocal(resultRepository, null);

  if (providedContext) {
    return providedContext;
  }

  const toProvide = getData(id);
  provideLocal(resultRepository, toProvide);
  return toProvide;
}
